import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import { Grid, makeStyles, Typography, Box } from "@material-ui/core";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import UserStatusIcon from "../UserModal/statusIcon";
import { isNil } from "lodash";

const useStyles = makeStyles((theme) => ({
  maxWidth: {
    width: "100%",
  },
  vacationAlert: {
    backgroundColor: "#fff3e0",
    border: "1px solid #ffe082",
    borderRadius: 8,
    padding: "10px 14px",
    marginTop: 8,
    color: "#e65100",
    fontSize: "0.85rem",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  offlineNotice: {
    color: "#757575",
    fontSize: "0.8rem",
    marginTop: 6,
    fontStyle: "italic",
  },
  chipBadge: {
    fontSize: "0.7rem",
    backgroundColor: "#ff9800",
    color: "#fff",
    padding: "2px 8px",
    borderRadius: 12,
    fontWeight: "bold",
    marginLeft: 8,
  },
}));

const filterOptions = createFilterOptions({
  trim: true,
});

const TransferTicketModalCustom = ({ modalOpen, onClose, ticketid, ticket }) => {
  const history = useHistory();
  const [options, setOptions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const classes = useStyles();
  const { findAll: findAllQueues } = useQueues();
  const isMounted = useRef(true);
  const [msgTransfer, setMsgTransfer] = useState("");

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      const loadQueues = async () => {
        const list = await findAllQueues();
        setAllQueues(list);
        setQueues(list);
      };
      loadQueues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!modalOpen || searchParam.length < 1) {
      setLoading(false);
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/", {
            params: { searchParam },
          });
          setOptions(data.users);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };

      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const handleMsgTransferChange = (event) => {
    setMsgTransfer(event.target.value);
  };

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setSelectedUser(null);
  };

  // 🏖️ Checagem se o usuário selecionado está ausente / de férias
  const isUserOnVacation = (user) => {
    if (!user) return false;
    if (user.onVacation) return true;
    if (user.absenceType && user.absenceType !== "active") return true;
    if (user.vacationStart && user.vacationEnd) {
      const now = new Date();
      return now >= new Date(user.vacationStart) && now <= new Date(user.vacationEnd);
    }
    return false;
  };

  const selectedUserBlocked = isUserOnVacation(selectedUser);

  const handleSaveTicket = async (e) => {
    if (!ticketid) return;
    if (!selectedQueue || selectedQueue === "") return;

    if (selectedUserBlocked) {
      toastError({
        response: {
          data: {
            message: `O atendente ${selectedUser.name} encontra-se ausente/de férias e não pode receber atendimentos no momento.`,
          },
        },
      });
      return;
    }

    setLoading(true);
    try {
      let data = {};

      data.userId = !selectedUser ? null : selectedUser.id;
      data.status = !selectedUser ? "pending" : ticket.isGroup ? "group" : "open";
      data.queueId = selectedQueue;
      data.msgTransfer = msgTransfer ? msgTransfer : null;
      data.isTransfered = true;

      await api.put(`/tickets/${ticketid}`, data);
      setLoading(false);
      history.push(`/tickets/`);
      handleClose();
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  return (
    <Dialog open={modalOpen} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle id="form-dialog-title">
        {i18n.t("transferTicketModal.title")}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} xl={6}>
            <Autocomplete
              fullWidth
              getOptionLabel={(option) => `${option.name}`}
              onChange={(e, newValue) => {
                setSelectedUser(newValue);
                if (newValue != null && Array.isArray(newValue.queues)) {
                  if (newValue.queues.length === 1) {
                    setSelectedQueue(newValue.queues[0].id);
                  }
                  setQueues(newValue.queues);
                } else {
                  setQueues(allQueues);
                  setSelectedQueue("");
                }
              }}
              options={options}
              filterOptions={filterOptions}
              freeSolo
              autoHighlight
              noOptionsText={i18n.t("transferTicketModal.noOptions")}
              loading={loading}
              renderOption={(option) => {
                const onVacation = isUserOnVacation(option);
                const absenceLabel =
                  option.absenceType === "leave"
                    ? "Licença"
                    : option.absenceType === "dayoff"
                    ? "Folga"
                    : option.absenceType === "pause"
                    ? "Pausa"
                    : "Férias";

                return (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                    <span style={{ display: "flex", alignItems: "center" }}>
                      <UserStatusIcon user={option} />
                      <span style={{ marginLeft: 8 }}>{option.name}</span>
                    </span>
                    {onVacation && (
                      <span className={classes.chipBadge}>
                        {absenceLabel}
                      </span>
                    )}
                  </div>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={i18n.t("transferTicketModal.fieldLabel")}
                  variant="outlined"
                  autoFocus
                  onChange={(e) => setSearchParam(e.target.value)}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />

            {/* 🏖️ Aviso de Atendente de Férias / Ausente */}
            {selectedUser && selectedUserBlocked && (
              <Box className={classes.vacationAlert}>
                <span>🏖️</span>
                <span>
                  <strong>Atenção:</strong> {selectedUser.name} está{" "}
                  {selectedUser.absenceType === "leave"
                    ? "de licença médica"
                    : selectedUser.absenceType === "dayoff"
                    ? "de folga"
                    : selectedUser.absenceType === "pause"
                    ? "em pausa"
                    : "de férias"}
                  {selectedUser.vacationEnd
                    ? ` até ${new Date(selectedUser.vacationEnd).toLocaleDateString("pt-BR")}`
                    : ""}
                  {selectedUser.absenceReason ? ` (${selectedUser.absenceReason})` : ""}.
                  <br />
                  <em>Transferência direta bloqueada. Escolha outro atendente ou transfira apenas para a fila.</em>
                </span>
              </Box>
            )}

            {/* ⚠️ Aviso informativo se estiver Offline */}
            {selectedUser && !selectedUserBlocked && selectedUser.online === false && (
              <Typography className={classes.offlineNotice}>
                ⚠️ Este atendente está desconectado (offline) no momento.
              </Typography>
            )}
          </Grid>
          <Grid xs={12} sm={6} xl={6} item>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>
                {i18n.t("transferTicketModal.fieldQueueLabel")}
              </InputLabel>
              <Select
                value={selectedQueue}
                onChange={(e) => setSelectedQueue(e.target.value)}
                label={i18n.t("transferTicketModal.fieldQueuePlaceholder")}
              >
                {queues.map((queue) => (
                  <MenuItem key={queue.id} value={queue.id}>
                    {queue.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} xl={12}>
            <TextField
              label={i18n.t("transferTicketModal.msgTransfer")}
              value={msgTransfer}
              onChange={handleMsgTransferChange}
              variant="outlined"
              multiline
              maxRows={5}
              minRows={5}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="secondary"
          disabled={loading}
          variant="outlined"
        >
          {i18n.t("transferTicketModal.buttons.cancel")}
        </Button>
        <ButtonWithSpinner
          variant="contained"
          type="submit"
          color="primary"
          loading={loading}
          disabled={selectedQueue === "" || selectedUserBlocked}
          onClick={() => handleSaveTicket(selectedQueue)}
        >
          {selectedUserBlocked ? "Atendente Indisponível" : i18n.t("transferTicketModal.buttons.ok")}
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default TransferTicketModalCustom;
